import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { ProductImage } from 'types'
import IconButton from 'components/buttons/iconButton'
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/keyboard_double_arrow_left.svg'
import { ReactComponent as ArrowRightIcon } from 'assets/icons/keyboard_double_arrow_right.svg'
import { ReactComponent as FullscreenIcon } from 'assets/icons/fullscreen.svg'
import { ReactComponent as FullscreenExitIcon } from 'assets/icons/fullscreen_exit.svg'
import useLayout from 'hooks/useLayout'

interface ProductImageGalleryProps {
    images: ProductImage[]
    productName: string
}

const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({ images, productName }) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const { galleryFullscreen, showGalleryFullscreen, hideGalleryFullscreen } = useLayout()

    if (!images || images.length === 0) {
        return null
    }

    // Sort images by order (first image is primary)
    const sortedImages = [...images].sort((a, b) => a.order - b.order)

    const goToPrevious = () => {
        setCurrentIndex((prevIndex) => 
            prevIndex === 0 ? sortedImages.length - 1 : prevIndex - 1
        )
    }

    const goToNext = () => {
        setCurrentIndex((prevIndex) => 
            prevIndex === sortedImages.length - 1 ? 0 : prevIndex + 1
        )
    }

    const goToSlide = (index: number) => {
        setCurrentIndex(index)
    }

    const toggleFullscreen = () => {
        if (galleryFullscreen) {
            hideGalleryFullscreen()
        } else {
            showGalleryFullscreen()
        }
    }

    // Fullscreen element
    const fullscreenElement = (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-[200] flex items-center justify-center">
            <div className="relative w-full h-full flex items-center justify-center p-4">
                <img 
                    src={sortedImages[currentIndex].url} 
                    alt={`${productName} - Image ${currentIndex + 1}`}
                    className="max-w-full max-h-full object-contain p-4 lg:p-0"
                />
                
                {sortedImages.length > 1 && (
                    <>
                        <div className="absolute inset-y-0 left-4 flex items-center">
                            <IconButton
                                icon={<ArrowLeftIcon />}
                                onClick={goToPrevious}
                                size="large"
                                colorScheme="gray"
                            />
                        </div>
                        <div className="absolute inset-y-0 right-4 flex items-center">
                            <IconButton
                                icon={<ArrowRightIcon />}
                                onClick={goToNext}
                                size="large"
                                colorScheme="gray"
                            />
                        </div>
                    </>
                )}

                {/* Close Fullscreen */}
                <div className="absolute top-4 right-4">
                    <IconButton
                        icon={<FullscreenExitIcon />}
                        onClick={toggleFullscreen}
                        size="large"
                        colorScheme="gray"
                    />
                </div>

                {/* Fullscreen Image Counter */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white px-3 py-2 rounded text-lg">
                    {currentIndex + 1} / {sortedImages.length}
                </div>

                {/* Fullscreen Thumbnail Navigation */}
                {sortedImages.length > 1 && <div className="absolute bottom-16 left-4 right-4 flex justify-center">
                    <div className="flex gap-2 overflow-x-auto max-w-full pb-2">
                        {sortedImages.map((image, index) => (
                            <button
                                key={index}
                                onClick={() => goToSlide(index)}
                                className={`flex-shrink-0 w-12 lg:w-20 h-12 lg:h-20 rounded-md overflow-hidden border-2 transition-all ${
                                    index === currentIndex 
                                        ? 'border-blue-500 opacity-100' 
                                        : 'border-gray-300 opacity-60 hover:opacity-80'
                                }`}
                            >
                                <img 
                                    src={image.url} 
                                    alt={`${productName} - Thumbnail ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />
                            </button>
                        ))}
                    </div>
                </div>}
            </div>
        </div>
    )

    // Single image view
    if (images.length === 1) {
        return (
            <>
                <div className="bg-gray-200 p-2 rounded-lg relative">
                    <img 
                        src={sortedImages[0].url} 
                        alt={productName}
                        className="max-w-full max-h-96 object-contain rounded-lg block mx-auto"
                    />
                    <div className="absolute top-4 right-4">
                        <IconButton
                            icon={<FullscreenIcon />}
                            onClick={toggleFullscreen}
                            size="medium"
                            colorScheme="gray"
                        />
                    </div>
                </div>
                {galleryFullscreen && createPortal(fullscreenElement, document.body)}
            </>
        )
    }

    // Multiple images gallery
    return (
        <>
            {/* Main Gallery */}
            <div className="bg-gray-100 p-2 rounded-lg relative max-w-lg">
                {/* Main Image Display */}
                <div className="relative mb-4">
                    <div className="overflow-hidden rounded-lg">
                        <img 
                            src={sortedImages[currentIndex].url} 
                            alt={`${productName} - Image ${currentIndex + 1}`}
                            className="w-full max-h-48 md:max-h-96 object-contain transition-all duration-500 ease-in-out"
                        />
                    </div>
                    
                    {/* Navigation Arrows */}
                    <div className="absolute inset-y-0 left-0 md:left-2 flex items-center">
                        <IconButton
                            icon={<ArrowLeftIcon />}
                            onClick={goToPrevious}
                            size="medium"
                            colorScheme="gray"
                        />
                    </div>
                    <div className="absolute inset-y-0 right-0 md:right-2 flex items-center">
                        <IconButton
                            icon={<ArrowRightIcon />}
                            onClick={goToNext}
                            size="medium"
                            colorScheme="gray"
                        />
                    </div>

                    {/* Fullscreen Button */}
                    <div className="absolute top-0 md:top-4 right-0 md:right-4">
                        <IconButton
                            icon={<FullscreenIcon />}
                            onClick={toggleFullscreen}
                            size="medium"
                            colorScheme="gray"
                        />
                    </div>

                    {/* Image Counter */}
                    <div className="absolute bottom-4 left-4 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-sm">
                        {currentIndex + 1} / {sortedImages.length}
                    </div>
                </div>

                {/* Thumbnail Navigation */}
                <div className="flex gap-2 overflow-x-auto pb-2">
                    {sortedImages.map((image, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${
                                index === currentIndex 
                                    ? 'border-blue-500 opacity-100' 
                                    : 'border-gray-300 opacity-60 hover:opacity-80'
                            }`}
                        >
                            <img 
                                src={image.url} 
                                alt={`${productName} - Thumbnail ${index + 1}`}
                                className="w-full h-full object-cover"
                            />
                        </button>
                    ))}
                </div>
            </div>

            {/* Fullscreen Modal */}
            {galleryFullscreen && createPortal(fullscreenElement, document.body)}
        </>
    )
}

export default ProductImageGallery

